import { EMOTION_KEYS } from './emotionConstants';

/**
 * Unwrap API response - handles various response shapes from Flask API
 * @param {Object} apiResult - Raw API response
 * @returns {Object} - Unwrapped response with RES/top_5/etc.
 */
export function unwrapApiResponse(apiResult) {
  return (apiResult && (apiResult.RES || apiResult.res || apiResult.data || apiResult)) || {};
}

/**
 * Parse a top_5 string like "happy 45% sad 20% angry 15% fear 10% disgust 5% surprise 5%"
 * into an object like { happy: 45, sad: 20, angry: 15, ... } (raw percentages 0-100)
 */
export function parseTop5Percentages(top5String) {
  if (!top5String || typeof top5String !== 'string') return {};
  const result = {};
  const regex = /(\w+)\s+(\d+(?:\.\d+)?)\s*%/g;
  let match;
  while ((match = regex.exec(top5String)) !== null) {
    const [, emotion, pct] = match;
    result[emotion.toLowerCase()] = parseFloat(pct);
  }
  return result;
}

/**
 * Parse top_5 string into EMOTION_KEYS-aligned scores (0-1 scale)
 * @param {string} top5String - Raw top_5 string
 * @returns {Object} - { happy: 0.45, sad: 0.20, ... } aligned to EMOTION_KEYS
 */
export function parseTop5Scores(top5String) {
  const scores = {};
  EMOTION_KEYS.forEach(em => {
    try {
      const re = new RegExp(`${em}\\s+(\\d+(?:\\.\\d+)?)%`, 'i');
      const m = (top5String || '').match(re);
      scores[em] = m ? parseFloat(m[1]) / 100 : 0;
    } catch {
      scores[em] = 0;
    }
  });
  return scores;
}

/**
 * Normalize emotion percentages (0-100) so they sum to 100
 */
export function normalizePercentages(emotions) {
  const total = Object.values(emotions).reduce((a, b) => a + b, 0);
  if (total === 0) return emotions;
  const normalized = {};
  for (const [key, val] of Object.entries(emotions)) {
    normalized[key] = (val / total) * 100;
  }
  return normalized;
}

/**
 * Map raw API emotion response to standard dashboard emotion keys
 * Returns scores in 0-1 scale aligned to EMOTION_KEYS
 */
export function mapApiToScores(apiResult) {
  const res = unwrapApiResponse(apiResult);
  return parseTop5Scores(res.top_5 || '');
}

/**
 * Map raw API emotion response to display-friendly keys used by enrollment pages
 * Returns { calm, happy, stressed, demotivated, anxious, hurt, burnout }
 */
export function mapApiToDisplayMetrics(apiResult) {
  const scores = mapApiToScores(apiResult);
  const calm = scores.happy ?? 0;
  const happy = scores.happy ?? 0;
  const stressed = scores.angry ?? 0;
  const demotivated = scores.sad ?? 0;
  const anxious = scores.fear ?? 0;
  const hurt = scores.disgust ?? 0;
  const burnout = Math.round(((scores.angry || 0) + (scores.sad || 0) + (scores.fear || 0)) * 100) || 0;
  return { calm, happy, stressed, demotivated, anxious, hurt, burnout };
}

/**
 * Map raw API emotion response to percentage values for charts (0-100 scale)
 * Returns object with raw emotion name keys
 */
export function mapApiToPercentages(apiResult) {
  const res = unwrapApiResponse(apiResult);
  const top5String = res.top_5 || '';
  return parseTop5Percentages(top5String);
}

/**
 * Get the dominant emotion from an emotions object
 * @param {Object} emotions - { happy: 0.45, sad: 0.20, ... }
 * @returns {string|null} - Key of the highest value
 */
export function getDominantEmotion(emotions) {
  if (!emotions || typeof emotions !== 'object') return null;
  let maxKey = null;
  let maxVal = -1;
  for (const [key, val] of Object.entries(emotions)) {
    if (val > maxVal) {
      maxVal = val;
      maxKey = key;
    }
  }
  return maxKey;
}

/**
 * Check if API result likely represents noise (low confidence / no clear emotions)
 * Used by audioAnalysis to filter out non-speech recordings
 */
export function isLikelyNoiseResult(apiData) {
  try {
    const res = unwrapApiResponse(apiData);
    const top5 = String(res.top_5 || '');
    const percentages = parseTop5Percentages(top5);
    const values = Object.values(percentages).filter(v => Number.isFinite(v));
    if (!values.length) return true;
    const maxVal = Math.max(...values);
    const above10 = values.filter(v => v >= 10).length;
    const sum = values.reduce((a, b) => a + b, 0);
    if (maxVal < 18 && above10 <= 1) return true;
    if (sum < 60) return true;
    return false;
  } catch {
    return false;
  }
}
